import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InstanceContext } from '@/contexts/InstanceContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { ActivityApi, EVALUATION_NOTE_MAX, tagsForAnswer } from '@api';
import type {
  ActivityEvaluationInput,
  EvaluationEnergy,
  EvaluationStatus,
  EvaluationTag,
} from '@api';

/**
 * Utvärderingen efter en avklarad aktivitet (FORSLAG-UTVARDERING-OCH-STATISTIK.md, 4.1).
 *
 * - standard (tonåring, vuxen): känslan på 5-skalan, sedan energi, sedan frivilligt vad
 *   som bidrog. Varje steg visas först när det förra är besvarat, så att det aldrig står
 *   mer än en fråga i taget, och steg 2 ligger bakom en egen knapp. Taggarna följer svaret
 *   (tagsForAnswer), och "Något annat" öppnar ett fält för egna ord. De orden är privata:
 *   fältet visas inte för en handledare, och backend lämnar aldrig ut dem.
 * - child: tre bilder (glad, sådär, ledsen), sparade som 5, 3 och 1 så att statistiken
 *   går att jämföra med 5-skalan. Ingen energifråga och inga taggar.
 *
 * "Vill inte svara" finns i båda och sparas som ett eget svarsläge, inte som en nolla.
 * Varje val sparas direkt, som förut.
 */

type Variant = 'standard' | 'child';

type Draft = {
  status: EvaluationStatus | null;
  mood: number | null;
  energy: EvaluationEnergy | null;
  tags: EvaluationTag[];
  note: string | null;
};

const EMPTY: Draft = { status: null, mood: null, energy: null, tags: [], note: null };

/** Taggar som inte passar det nya svaret tas bort, i stället för att sparas osynliga. */
const withFittingTags = (next: Draft): Draft => {
  const fitting = tagsForAnswer(next.mood, next.energy);
  return { ...next, tags: next.tags.filter((tag) => fitting.includes(tag)) };
};
const MOODS = [1, 2, 3, 4, 5];
const CHILD_MOODS: ReadonlyArray<{ mood: number; key: 'happy' | 'ok' | 'sad' }> = [
  { mood: 5, key: 'happy' },
  { mood: 3, key: 'ok' },
  { mood: 1, key: 'sad' },
];
const ENERGIES: readonly EvaluationEnergy[] = ['giving', 'neutral', 'draining'];

const ActivityEvaluation = ({ variant = 'standard' }: { variant?: Variant }) => {
  const { t } = useTranslation();
  const { instance, setInstance } = useContext(InstanceContext);
  const { currentClient } = useContext(SupervisorContext);
  const supervisedTag = currentClient?.tag;
  const instanceId = instance?.activityInstanceId;

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [showTags, setShowTags] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  // Egna ord är brukarens privata. En handledare som svarar åt brukaren får inget fält.
  const canWriteNote = supervisedTag === undefined;
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'failed'>('idle');

  useEffect(() => {
    if (instanceId === undefined) return;
    let cancelled = false;
    new ActivityApi().getActivityEvaluation(instanceId, supervisedTag).then((saved) => {
      if (cancelled || !saved) return;
      setDraft({
        status: saved.status,
        mood: saved.mood,
        energy: saved.energy,
        tags: saved.tags,
        note: saved.note,
      });
      setNoteText(saved.note ?? '');
      if (saved.tags.length > 0 || saved.note) setShowTags(true);
      if (saved.note) setShowNote(true);
    });
    return () => {
      cancelled = true;
    };
  }, [instanceId, supervisedTag]);

  const save = async (next: Draft) => {
    if (instanceId === undefined || (next.status !== 'answered' && next.status !== 'declined'))
      return;
    const previous = draft;
    setDraft(next);
    const input: ActivityEvaluationInput =
      next.status === 'declined'
        ? { status: 'declined' }
        : {
            status: 'answered',
            mood: next.mood,
            energy: next.energy,
            tags: next.tags,
            ...(canWriteNote ? { note: next.note } : {}),
          };
    try {
      await new ActivityApi().setActivityEvaluation(instanceId, input, supervisedTag);
      setInstance({ ...instance, feelingOfActivity: next.mood ?? 0 });
      setSaveState('saved');
    } catch {
      // Valet backas: det som syns ska vara det som är sparat.
      setDraft(previous);
      setSaveState('failed');
    }
  };

  const answered = draft.status === 'answered';
  const pickMood = (mood: number) =>
    save(
      withFittingTags({
        ...draft,
        status: 'answered',
        mood,
        ...(draft.status === 'declined' ? { energy: null, tags: [], note: null } : {}),
      }),
    );
  const pickEnergy = (energy: EvaluationEnergy) =>
    save(withFittingTags({ ...draft, energy: draft.energy === energy ? null : energy }));
  const saveNote = () => save({ ...draft, note: noteText.trim() || null });
  const toggleTag = (tag: EvaluationTag) =>
    save({
      ...draft,
      tags: draft.tags.includes(tag)
        ? draft.tags.filter((item) => item !== tag)
        : [...draft.tags, tag],
    });
  const decline = () => {
    setShowTags(false);
    setShowNote(false);
    setNoteText('');
    save({ ...EMPTY, status: 'declined' });
  };

  const selectedShadow = 'grey 0px 2px 0.45em 0.01em';

  return (
    <Stack spacing={2} pb={3} alignItems="center" sx={{ width: '100%' }}>
      {variant === 'child' ? (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          role="group"
          aria-label={t('evaluation.child.question') as string}
        >
          {CHILD_MOODS.map(({ mood, key }) => {
            const selected = answered && draft.mood === mood;
            return (
              <Button
                key={key}
                onClick={() => pickMood(mood)}
                aria-pressed={selected}
                sx={{ flexDirection: 'column', gap: 0.5, minWidth: 0, p: 1, borderRadius: '16px' }}
              >
                <Box
                  component="img"
                  src={`/emotions/${mood}.png`}
                  alt=""
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    boxShadow: selected ? selectedShadow : 'none',
                    transform: selected ? 'scale(1.15)' : 'scale(1)',
                    transition: 'transform 0.1s ease-out',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
                <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                  {t(`evaluation.child.${key}`)}
                </Typography>
              </Button>
            );
          })}
        </Stack>
      ) : (
        <>
          <Stack alignItems="center">
            <Typography variant="body1" fontWeight="bold" id="evaluation-mood-question">
              {t('evaluation.moodQuestion')}
            </Typography>
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {answered && draft.mood !== null ? t(`activityFeeling.${draft.mood}`) : null}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-evenly"
            sx={{ maxWidth: '100%' }}
            role="group"
            aria-labelledby="evaluation-mood-question"
          >
            {MOODS.map((mood) => {
              const selected = answered && draft.mood === mood;
              return (
                <Button
                  key={mood}
                  onClick={() => pickMood(mood)}
                  variant="text"
                  aria-pressed={selected}
                  aria-label={t(`activityFeeling.${mood}`) as string}
                  title={t(`activityFeeling.${mood}`) as string}
                  sx={{
                    minWidth: 0,
                    width: '100%',
                    flexGrow: 1,
                    p: 0.75,
                    borderRadius: '50%',
                    transition: 'transform 0.05s ease-out',
                    transform: selected ? 'scale(1.25)' : 'scale(1)',
                  }}
                >
                  <img
                    src={`/emotions/${mood}.png`}
                    alt=""
                    height="100%"
                    width="100%"
                    style={{ borderRadius: '50%', boxShadow: selected ? selectedShadow : 'none' }}
                  />
                </Button>
              );
            })}
          </Stack>

          {answered && (
            <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <Typography variant="body1" fontWeight="bold" id="evaluation-energy-question">
                {t('evaluation.energyQuestion')}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
                role="group"
                aria-labelledby="evaluation-energy-question"
              >
                {ENERGIES.map((energy) => (
                  <Button
                    key={energy}
                    onClick={() => pickEnergy(energy)}
                    variant={draft.energy === energy ? 'contained' : 'outlined'}
                    aria-pressed={draft.energy === energy}
                  >
                    {t(`evaluation.energy.${energy}`)}
                  </Button>
                ))}
              </Stack>
            </Stack>
          )}

          {answered && !showTags && (
            <Button variant="text" onClick={() => setShowTags(true)}>
              {t('evaluation.tagsToggle')}
            </Button>
          )}

          {answered && showTags && (
            <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight="bold" id="evaluation-tags-question">
                {t('evaluation.tagsQuestion')}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
                role="group"
                aria-labelledby="evaluation-tags-question"
              >
                {tagsForAnswer(draft.mood, draft.energy).map((tag) => {
                  const selected = draft.tags.includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={t(`evaluation.tag.${tag}`)}
                      onClick={() => toggleTag(tag)}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      aria-pressed={selected}
                    />
                  );
                })}
                {canWriteNote && !showNote && (
                  <Chip
                    label={t('evaluation.noteToggle')}
                    onClick={() => setShowNote(true)}
                    variant="outlined"
                  />
                )}
              </Stack>
              {canWriteNote && showNote && (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <TextField
                    label={t('evaluation.noteLabel')}
                    value={noteText}
                    onChange={(event) =>
                      setNoteText(event.target.value.slice(0, EVALUATION_NOTE_MAX))
                    }
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: EVALUATION_NOTE_MAX }}
                    helperText={`${t('evaluation.notePrivate')} ${
                      noteText.length
                    }/${EVALUATION_NOTE_MAX}`}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={saveNote}
                    disabled={(noteText.trim() || null) === draft.note}
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    {t('evaluation.noteSave')}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </>
      )}

      <Button
        onClick={decline}
        variant="outlined"
        aria-pressed={draft.status === 'declined'}
        sx={{ boxShadow: draft.status === 'declined' ? selectedShadow : 'none' }}
      >
        {t("I don't want to specify")}
      </Button>

      <Typography
        variant="body2"
        role="status"
        aria-live="polite"
        color={saveState === 'failed' ? 'error' : 'text.secondary'}
        sx={{ minHeight: '1.5em' }}
      >
        {saveState === 'saved'
          ? t('evaluation.saved')
          : saveState === 'failed'
          ? t('evaluation.saveFailed')
          : ''}
      </Typography>
    </Stack>
  );
};

export default ActivityEvaluation;
